import { useTranslation } from 'react-i18next';

const en = {
  campaign: 'El Gato × Super Hero',
  campaignTag: 'The clipper competition',
  headline: 'SUPER HERO',
  tagline: 'Your creativity is your superpower.',
  introduction: 'Turn your best moments into clips. Put your talent in the game.',
  artworkAlt: 'El Gato × Super Hero campaign artwork',
  logoAlt: 'El Gato, Super Hero campaign',
  prize: 'R$25,000',
  prizeLabel: 'in fixed prizes',
  places: '20',
  placesLabel: 'prize positions',
  payment: 'Prizes paid by the organizer.',
  rules: 'Ranking by reviewed views, subject to the campaign rules. Account access does not enter you in the competition.',
  preview: 'Private preview',
  comingSoon: 'Starting soon',
  entryNotice: 'Sign in to view the campaign and join.',
  previewDescription: 'This campaign is starting soon. It has not started yet.',
  poweredBy: 'Account powered by Autoclipper',
  accountStep: '01 / YOUR ACCOUNT',
  loginTitle: 'Your next move starts here.',
  registerTitle: 'Make room for your talent.',
  loginDescription: 'Sign in to continue straight to the campaign.',
  registerDescription: 'Create your Autoclipper account to check campaign access.',
  loginMode: 'Sign in',
  registerMode: 'Create account',
  google: 'Continue with Google',
  orEmail: 'or use your email',
  email: 'Email',
  emailPlaceholder: 'you@example.com',
  password: 'Password',
  confirmPassword: 'Confirm password',
  name: 'Full name',
  phone: 'Phone number',
  showPassword: 'Show password',
  hidePassword: 'Hide password',
  showConfirmation: 'Show password confirmation',
  hideConfirmation: 'Hide password confirmation',
  forgotPassword: 'Forgot password?',
  loginSubmit: 'Sign in and continue',
  registerSubmit: 'Create account and continue',
  submitting: 'Please wait…',
  marketing: 'Send me Autoclipper news, tips and offers by email.',
  tosBefore: 'By creating an account, you agree to the ',
  terms: 'Terms of Service',
  tosBetween: ' and ',
  privacy: 'Privacy Policy',
  tosAfter: '.',
  checkingSession: 'Checking your account…',
  completing: 'Getting your campaign access ready…',
  completingDetail: 'One moment. We are checking availability for your account.',
  signedIn: 'SIGNED IN',
  unavailableTitle: 'The campaign is starting soon.',
  unavailable: 'The campaign has not started yet. Check back soon to participate.',
  entryFailedTitle: 'We could not finish your entry.',
  entryFailed: 'Your campaign access could not be prepared. Try again in a moment.',
  staleSession: 'Your session has changed. Try again with your current account.',
  sessionFailed: 'We could not verify your session. Reconnect to continue.',
  retry: 'Try again',
  account: 'Go to my account',
  loginFailed: 'Could not sign in. Check your email and password, then try again.',
  registerFailed: 'Could not create your account. Please try again in a moment.',
  emailTaken: 'This email already has an account. Sign in to continue.',
  invalidEmail: 'Enter a valid email address.',
  passwordMismatch: 'The passwords do not match.',
  footer: 'Your account. Your clips. Your next level.',
  sidebarName: 'El Gato',
  sidebarBadge: 'New competition',
  sidebarCta: 'View campaign',
  sidebarLabel: 'El Gato — view campaign',
  sidebarPreviewLabel: 'El Gato — new competition',
} as const;

type CampaignEntryCopy = { [Key in keyof typeof en]: string };

const pt: CampaignEntryCopy = {
  campaign: 'El Gato × Super Hero',
  campaignTag: 'A competição dos clipadores',
  headline: 'SUPER HERO',
  tagline: 'Sua criatividade também é arma.',
  introduction: 'Transforme os melhores momentos em cortes. Coloque seu talento em jogo.',
  artworkAlt: 'Arte da campanha El Gato × Super Hero',
  logoAlt: 'El Gato, campanha Super Hero',
  prize: 'R$25.000',
  prizeLabel: 'em prêmios fixos',
  places: '20',
  placesLabel: 'posições premiadas',
  payment: 'Premiação paga pelo organizador.',
  rules: 'Ranking por visualizações revisadas, conforme o regulamento. Acessar sua conta não inscreve você na competição.',
  preview: 'Prévia privada',
  comingSoon: 'Em breve',
  entryNotice: 'Entre na sua conta para ver a campanha e participar.',
  previewDescription: 'Esta campanha vai começar em breve. Ela ainda não foi iniciada.',
  poweredBy: 'Sua conta com a Autoclipper',
  accountStep: '01 / SUA CONTA',
  loginTitle: 'A próxima jogada é sua.',
  registerTitle: 'Seu talento merece espaço.',
  loginDescription: 'Entre para continuar direto para a campanha.',
  registerDescription: 'Crie sua conta Autoclipper para verificar o acesso à campanha.',
  loginMode: 'Entrar',
  registerMode: 'Criar conta',
  google: 'Continuar com Google',
  orEmail: 'ou use seu email',
  email: 'Email',
  emailPlaceholder: 'voce@exemplo.com',
  password: 'Senha',
  confirmPassword: 'Confirmar senha',
  name: 'Nome completo',
  phone: 'Telefone',
  showPassword: 'Mostrar senha',
  hidePassword: 'Ocultar senha',
  showConfirmation: 'Mostrar confirmação de senha',
  hideConfirmation: 'Ocultar confirmação de senha',
  forgotPassword: 'Esqueceu a senha?',
  loginSubmit: 'Entrar e continuar',
  registerSubmit: 'Criar conta e continuar',
  submitting: 'Aguarde…',
  marketing: 'Quero receber novidades, dicas e ofertas da Autoclipper por email.',
  tosBefore: 'Ao criar sua conta, você concorda com os ',
  terms: 'Termos de Uso',
  tosBetween: ' e a ',
  privacy: 'Política de Privacidade',
  tosAfter: '.',
  checkingSession: 'Verificando sua conta…',
  completing: 'Preparando seu acesso à campanha…',
  completingDetail: 'Só um momento. Estamos verificando a disponibilidade para sua conta.',
  signedIn: 'CONECTADO',
  unavailableTitle: 'A campanha vai começar em breve.',
  unavailable: 'A campanha ainda não começou. Volte em breve para participar.',
  entryFailedTitle: 'Não foi possível concluir seu acesso.',
  entryFailed: 'Não foi possível preparar seu acesso à campanha. Tente novamente em instantes.',
  staleSession: 'Sua sessão mudou. Tente novamente com a conta atual.',
  sessionFailed: 'Não foi possível verificar sua sessão. Reconecte para continuar.',
  retry: 'Tentar novamente',
  account: 'Ir para minha conta',
  loginFailed: 'Não foi possível entrar. Confira seu email e sua senha e tente novamente.',
  registerFailed: 'Não foi possível criar sua conta. Tente novamente em instantes.',
  emailTaken: 'Este email já tem uma conta. Entre para continuar.',
  invalidEmail: 'Informe um endereço de email válido.',
  passwordMismatch: 'As senhas não coincidem.',
  footer: 'Sua conta. Seus cortes. Seu próximo nível.',
  sidebarName: 'El Gato',
  sidebarBadge: 'Nova Competição',
  sidebarCta: 'Ver campanha',
  sidebarLabel: 'El Gato — ver campanha',
  sidebarPreviewLabel: 'El Gato — nova competição',
};

/** Scoped bilingual copy follows the existing account/browser language. */
export function useCampaignEntryCopy(): CampaignEntryCopy {
  const { i18n } = useTranslation();
  return (i18n?.resolvedLanguage || i18n?.language || 'pt').startsWith('en') ? en : pt;
}
